/**
 * Typisierte Endpunkt-Funktionen. Eine Stelle, die die Backend-Routen kennt.
 * Screens/Stores rufen nur diese Funktionen auf, nie fetch direkt.
 */
import { api } from "./client";
import type {
  AccessCheckResponse,
  CertificateStatusResponse,
  ChapterResponse,
  ChatHistoryDeleteResponse,
  ChatHistoryResponse,
  ChatResponse,
  CoursesListResponse,
  LoginResponse,
  ManifestResponse,
  MeResponse,
  PasswordResetResponse,
  PersonalisierenRequest,
  PersonalisierenResponse,
  ProgressEventPayload,
  ProgressStateResponse,
  QuotaResponse,
  TranscribeResponse,
} from "./types";

export interface ChatPayload {
  message: string;
  course_id?: string;
  module_id?: string | null;
  block_id?: string | null;
  page?: string | null;
  recent_questions?: string[];
}

export interface RegisterPayload {
  email: string;
  password: string;
  full_name: string;
  preferred_name: string;
  company?: string;
  privacy_policy_accepted: boolean;
  terms_accepted: boolean;
}

export const endpoints = {
  // --- Auth ---
  accessCheck: (email: string) =>
    api.post<AccessCheckResponse>(
      "/api/auth/access-check",
      { email },
      { anonymous: true },
    ),
  login: (email: string, password: string) =>
    api.post<LoginResponse>(
      "/api/auth/login",
      { email, password },
      { anonymous: true },
    ),
  register: (payload: RegisterPayload) =>
    api.post<LoginResponse>("/api/auth/register", payload, { anonymous: true }),
  passwordResetRequest: (email: string) =>
    api.post<PasswordResetResponse>(
      "/api/auth/password-reset/request",
      { email },
      { anonymous: true },
    ),
  passwordResetConfirm: (token: string, password: string) =>
    api.post<PasswordResetResponse>(
      "/api/auth/password-reset/confirm",
      { token, password },
      { anonymous: true },
    ),
  logout: (token: string) => api.post<{ status: string }>("/api/auth/logout", { token }),
  // Eingeloggt: Passwort ändern (widerruft danach alle Sessions) / Account löschen.
  passwordChange: (current_password: string, new_password: string) =>
    api.post<PasswordResetResponse>("/api/auth/password-change", { current_password, new_password }),
  accountDelete: (password: string) =>
    api.post<PasswordResetResponse>("/api/auth/account-delete", { password }),
  me: () => api.get<MeResponse>("/api/me"),

  // --- Kurs ---
  courses: () => api.get<CoursesListResponse>("/api/courses"),
  manifest: (courseId: string) =>
    api.get<ManifestResponse>(`/api/courses/${encodeURIComponent(courseId)}/manifest`),
  chapter: (courseId: string, moduleId: string, blockId: string) =>
    api.get<ChapterResponse>(
      `/api/courses/${encodeURIComponent(courseId)}/chapters/${encodeURIComponent(
        moduleId,
      )}/${encodeURIComponent(blockId)}`,
    ),

  // --- Coach / Sprache ---
  chat: (payload: ChatPayload, signal?: AbortSignal) =>
    api.post<ChatResponse>("/api/chat", payload, { signal }),
  transcribe: (audio: Blob) =>
    api.postRaw<TranscribeResponse>("/api/transcribe", audio),

  // --- Coach: Chat-Verlauf (Nachlesen + Loeschen) ---
  // Letzte N Gespraechs-Sitzungen des Users (course-bezogen). 401 ohne Login.
  chatHistory: (courseId?: string, sessions = 3) =>
    api.get<ChatHistoryResponse>(
      `/api/chat/history?sessions=${encodeURIComponent(String(sessions))}` +
        (courseId ? `&course_id=${encodeURIComponent(courseId)}` : ""),
    ),
  // Eine Sitzung (id-Bereich) loeschen.
  chatHistoryDeleteSession: (fromId: number, toId: number, courseId?: string) =>
    api.del<ChatHistoryDeleteResponse>(
      `/api/chat/history?from_id=${encodeURIComponent(String(fromId))}&to_id=${encodeURIComponent(String(toId))}` +
        (courseId ? `&course_id=${encodeURIComponent(courseId)}` : ""),
    ),
  // Ganzen Verlauf loeschen (Doppel-Bestaetigung im UI, confirm=true ist Pflicht).
  chatHistoryDeleteAll: (courseId?: string) =>
    api.del<ChatHistoryDeleteResponse>(
      `/api/chat/history?confirm=true` +
        (courseId ? `&course_id=${encodeURIComponent(courseId)}` : ""),
    ),

  // --- Werkstatt-Personalisierung (W1) ---
  // Schreibt die Projekt-Prompts auf das Vorhaben des Nutzers um. Server speichert
  // NICHTS (Postkarten-Regel) — das Ergebnis liegt nur lokal im Browser.
  personalisieren: (payload: PersonalisierenRequest) =>
    api.post<PersonalisierenResponse>("/api/werkstatt/personalisieren", payload),

  // --- Fortschritt / Kontingent ---
  quota: () => api.get<QuotaResponse>("/api/quota"),

  // --- Stats (Trainer/Admin, rollen-gegated) ---
  statsUsage: () => api.get<Record<string, unknown>>("/api/stats/usage"),
  statsProgress: () => api.get<Record<string, unknown>>("/api/stats/progress"),
  progressEvent: (payload: ProgressEventPayload) =>
    api.post<{ status: string }>("/api/progress/event", payload),
  progressState: (courseId?: string) =>
    api.get<ProgressStateResponse>(
      `/api/progress/state${courseId ? `?course_id=${encodeURIComponent(courseId)}` : ""}`,
    ),

  // --- Abschluss-Zertifikat ---
  certificateStatus: (courseId?: string) =>
    api.get<CertificateStatusResponse>(
      `/api/certificate/status${courseId ? `?course_id=${encodeURIComponent(courseId)}` : ""}`,
    ),
};
